import { ConsoleID, getConsoleState } from 'app/config/Logger';
import { ActionCls, Cls, CountDisplayCls, Emojis, ValueCls } from 'app/constants';
import { Stores, SyncStoreProps } from 'types';

let request: IDBOpenDBRequest;
let db: IDBDatabase;
let version = 5;
const LOCAL_STORAGE_DB_VERSION_KEY = 'lastIDBVersion';

export const MY_NFL_STATS_DB = 'nflStatsDB';
const CONSOLE_ON = getConsoleState(ConsoleID.IndexDb);

export const getDbVersion = (): number => version;

/* eslint-disable @typescript-eslint/no-unused-vars */
const trStart = Emojis.transactionStart;
const trSuccess = Emojis.transactionSuccess;
const trEnd = Emojis.transactionComplete;
/* eslint-enable @typescript-eslint/no-unused-vars */

export const initDBVersion = async (callback: VoidFunction): Promise<void> => {
  const dataFromLocalStorage = localStorage.getItem(LOCAL_STORAGE_DB_VERSION_KEY);

  if (dataFromLocalStorage) {
    const parsedData = JSON.parse(dataFromLocalStorage) as DBLocalStorageVersion;
    if (CONSOLE_ON) console.log(`${Emojis.voiceAmp} IDB Database data from local storage: `, parsedData);
    // Use the higher version to ensure upgrades happen
    version = Math.max(parsedData.version, version);
  }

  await initDB(version, callback);
};

export const updateDbVersion = (newVersion: number): void => {
  const dbVersion: DBLocalStorageVersion = { version: newVersion, timestamp: new Date().toISOString() };
  localStorage.setItem('lastIDBVersion: ', JSON.stringify(dbVersion));
};

type DBLocalStorageVersion = {
  version: number;
  timestamp: string;
};

export const initDB = (newVersion: number, callback: VoidFunction): Promise<boolean> => {
  return new Promise((resolve) => {
    // open the connection
    request = indexedDB.open(MY_NFL_STATS_DB, newVersion);

    const processEvent = (event: Event) => {
      const db = (event.target as IDBRequest).result;

      // #region TEAM-INFO *****************************************************
      if (!db.objectStoreNames.contains(Stores.TeamInfo)) {
        if (CONSOLE_ON) console.log(`${Emojis.hammers} Creating TeamInfo store`);
        db.createObjectStore(Stores.TeamInfo, { keyPath: 'teamID' });
      } else {
        if (CONSOLE_ON) console.log(`${Emojis.voiceAmp} TeamInfo store already exists`);
      }
      // #endregion

      // #region GAME-SCHEDULE *************************************************
      if (!db.objectStoreNames.contains(Stores.GameSchedule)) {
        if (CONSOLE_ON) console.log(`${Emojis.hammers} Creating GameSchedule store`);
        const gameScheduleStore = db.createObjectStore(Stores.GameSchedule, { keyPath: 'gameID' });
        // Add compound index for gameWeek + season lookups
        gameScheduleStore.createIndex('weekSeason', ['gameWeek', 'season'], { unique: false });

        // Optional: Add individual indexes for flexibility
        gameScheduleStore.createIndex('seasonWeekID', 'seasonWeekID', { unique: false });
        gameScheduleStore.createIndex('gameWeek', 'gameWeek', { unique: false });
        gameScheduleStore.createIndex('season', 'season', { unique: false });
        if (CONSOLE_ON) console.log(`${Emojis.checkMark} %c GameSchedule %c and indexes created`, Cls.Loc, '');
      } else {
        if (CONSOLE_ON) console.log(`${Emojis.voiceAmp} GameSchedule store already exists`);
        const transaction = (event.target as IDBRequest)?.transaction;
        if (transaction) {
          const gameScheduleStore = transaction.objectStore(Stores.GameSchedule);

          // Check if compound index exists, if not create it
          if (!gameScheduleStore.indexNames.contains('weekSeason')) {
            gameScheduleStore.createIndex('weekSeason', ['gameWeek', 'season'], { unique: false });
            const log = `${Emojis.gear} Added weekSeason compound index to existing GameSchedule store`;
            if (CONSOLE_ON) console.log(log);
          }

          // Check if individual indexes exist
          if (!gameScheduleStore.indexNames.contains('seasonWeekID')) {
            gameScheduleStore.createIndex('seasonWeekID', 'seasonWeekID', { unique: false });
          }
          if (!gameScheduleStore.indexNames.contains('gameWeek')) {
            gameScheduleStore.createIndex('gameWeek', 'gameWeek', { unique: false });
          }
          if (!gameScheduleStore.indexNames.contains('season')) {
            gameScheduleStore.createIndex('season', 'season', { unique: false });
          }
        }
      }
      // #endregion

      // #region GAME-STATS ***************************************************
      // if the data object store doesn't exist, create it
      if (!db.objectStoreNames.contains(Stores.GameStats)) {
        if (CONSOLE_ON) console.log(`${Emojis.gear} Creating GameStats store`);
        const gameStatsStore = db.createObjectStore(Stores.GameStats, { keyPath: 'gameID' });
        gameStatsStore.createIndex('seasonWeekID', 'seasonWeekID', { unique: false });
        const msgProps = 'keyPath %c gameID %c and index %c seasonWeekID %c';
        const msg = `${Emojis.info} %c ${Stores.GameStats} %c store created with ${msgProps}`;
        if (CONSOLE_ON) console.log(msg, Cls.Info, '', Cls.Loc, '', Cls.Pale, '');
      } else {
        if (CONSOLE_ON) console.log(`${Emojis.voiceAmp} GameStats store already exists`);
        const transaction = (event.target as IDBRequest)?.transaction;
        if (transaction) {
          const gameStatsStore = transaction.objectStore(Stores.GameStats);

          // Check if compound index exists, if not create it
          if (!gameStatsStore.indexNames.contains('seasonWeekID')) {
            gameStatsStore.createIndex('seasonWeekID', 'seasonWeekID', { unique: false });
            if (CONSOLE_ON) console.log(`${Emojis.gear} Added seasonWeekID index to existing GameStats store`);
          }
        }
      }
      // #endregion

      // #region TEAM-RESULT **************************************************
      if (!db.objectStoreNames.contains(Stores.TeamWeeklyStats)) {
        if (CONSOLE_ON) console.log(`${Emojis.gear} Creating TeamWeeklyStats store`);
        const teamWeeklyStatsStore = db.createObjectStore(Stores.TeamWeeklyStats, { keyPath: 'uID' });
        teamWeeklyStatsStore.createIndex('seasonWeekID', 'seasonWeekID', { unique: false });
      } else {
        if (CONSOLE_ON) console.log(`${Emojis.voiceAmp} TeamWeeklyStats store already exists`);
        // db.deleteObjectStore(Stores.TeamWeeklyStats);
        // db.createObjectStore(Stores.TeamWeeklyStats, { keyPath: 'uID' });
        const transaction = (event.target as IDBRequest)?.transaction;
        if (transaction) {
          const teamWeeklyStatsStore = transaction.objectStore(Stores.TeamWeeklyStats);

          // Check if compound index exists, if not create it
          if (!teamWeeklyStatsStore.indexNames.contains('seasonWeekID')) {
            teamWeeklyStatsStore.createIndex('seasonWeekID', 'seasonWeekID', { unique: false });
            if (CONSOLE_ON) console.log(`${Emojis.gear} Added seasonWeekID index to existing TeamWeeklyStats store`);
          }
        }
      }
      // #endregion

      // #region TEAM-ON-BYE-WEEK *********************************************
      // db.deleteObjectStore(Stores.TeamsOnByeWeek);
      if (!db.objectStoreNames.contains(Stores.TeamsOnByeWeek)) {
        if (CONSOLE_ON) console.log(`${Emojis.gear} Creating TeamsOnByeWeek store`);
        db.createObjectStore(Stores.TeamsOnByeWeek, { keyPath: 'seasonWeekID' });
      } else {
        if (CONSOLE_ON) console.log(`${Emojis.voiceAmp} TeamsOnByeWeek store already exists`);
      }
      // #endregion

      // #region RANKS ********************************************************
      if (!db.objectStoreNames.contains(Stores.WeeklyRanks)) {
        if (CONSOLE_ON) console.log(`${Emojis.gear} Creating WeeklyRanks store`);
        db.createObjectStore(Stores.WeeklyRanks, { keyPath: 'seasonWeekID' });
      } else {
        if (CONSOLE_ON) console.log(`${Emojis.voiceAmp} WeeklyRanks store already exists`);
        // db.deleteObjectStore(Stores.WeeklyRanks);
        // db.createObjectStore(Stores.WeeklyRanks, { keyPath: 'seasonWeekID' });
        // deleteStore(Stores.WeeklyRanks, version + 1);
      }
      // #endregion

      // #region STANDINGS ****************************************************
      if (!db.objectStoreNames.contains(Stores.SeasonStandings)) {
        if (CONSOLE_ON) console.log(`${Emojis.gear} Creating SeasonStandings store`);
        const seasonStandingsStore = db.createObjectStore(Stores.SeasonStandings, { keyPath: 'uID' });
        seasonStandingsStore.createIndex('seasonWeekID', 'seasonWeekID', { unique: false });
      } else {
        if (CONSOLE_ON) console.log(`${Emojis.voiceAmp} SeasonStandings store already exists`);
        const transaction = (event.target as IDBRequest)?.transaction;
        if (transaction) {
          const seasonStandingsStore = transaction.objectStore(Stores.SeasonStandings);
          // Check if compound index exists, if not create it
          if (!seasonStandingsStore.indexNames.contains('seasonWeekID')) {
            seasonStandingsStore.createIndex('seasonWeekID', 'seasonWeekID', { unique: false });
            if (CONSOLE_ON) console.log(`${Emojis.gear} Added seasonWeekID index to existing SeasonStandings store`);
          }
        }
      }
      // #endregion

      // #region PREDICTIONS **************************************************
      if (!db.objectStoreNames.contains(Stores.Predictions)) {
        if (CONSOLE_ON) console.log(`${Emojis.gear} Creating Predictions store`);
        const predictionsStore = db.createObjectStore(Stores.Predictions, { keyPath: ['gameID', 'modelVersion'] });
        predictionsStore.createIndex('seasonWeekID', 'seasonWeekID', { unique: false });
        predictionsStore.createIndex('modelVersion', 'modelVersion', { unique: false });
      } else {
        if (CONSOLE_ON) console.log(`${Emojis.voiceAmp} Predictions store already exists`);
      }
      // #endregion

      // #region CATEGORY-RANKINGS ********************************************
      if (!db.objectStoreNames.contains(Stores.CategoryRankings)) {
        if (CONSOLE_ON) console.log(`${Emojis.gear} Creating CategoryRankings store`);
        const categoryRankingsStore = db.createObjectStore(Stores.CategoryRankings, {
          keyPath: ['seasonWeekID', 'category'],
        });
        categoryRankingsStore.createIndex('seasonWeekID', 'seasonWeekID', { unique: false });
        categoryRankingsStore.createIndex('category', 'category', { unique: false });
      } else {
        if (CONSOLE_ON) console.log(`${Emojis.voiceAmp} CategoryRankings store already exists`);
      }
      // #endregion

      // #region TEAM-SEASON-STATS *******************************************
      if (!db.objectStoreNames.contains(Stores.TeamSeasonStats)) {
        if (CONSOLE_ON) console.log(`${Emojis.gear} Creating TeamSeasonStats store`);
        const teamSeasonStatsStore = db.createObjectStore(Stores.TeamSeasonStats, {
          keyPath: 'seasonTeamAbrvID',
        });
        teamSeasonStatsStore.createIndex('teamAbrvID', 'teamAbrvID', { unique: false });
        teamSeasonStatsStore.createIndex('seasonID', 'seasonID', { unique: false });
      } else {
        if (CONSOLE_ON) console.log(`${Emojis.voiceAmp} TeamSeasonStats store already exists`);
      }
      // #endregion

      // #region TEAM-CUMULATIVE-STATS ***************************************
      if (!db.objectStoreNames.contains(Stores.TeamCumulativeStats)) {
        if (CONSOLE_ON) console.log(`${Emojis.gear} Creating TeamCumulativeStats store`);
        const teamCumulativeStatsStore = db.createObjectStore(Stores.TeamCumulativeStats, {
          keyPath: 'seasonTeamAbrvID',
        });
        teamCumulativeStatsStore.createIndex('teamAbrvID', 'teamAbrvID', { unique: false });
        teamCumulativeStatsStore.createIndex('seasonID', 'seasonID', { unique: false });
      } else {
        if (CONSOLE_ON) console.log(`${Emojis.voiceAmp} TeamCumulativeStats store already exists`);
      }
      // #endregion

      // #region TEAM-SEASON-RANKS *******************************************
      if (!db.objectStoreNames.contains(Stores.TeamSeasonRanks)) {
        if (CONSOLE_ON) console.log(`${Emojis.gear} Creating TeamSeasonRanks store`);
        const teamSeasonRanksStore = db.createObjectStore(Stores.TeamSeasonRanks, {
          keyPath: 'seasonTeamAbrvID',
        });
        teamSeasonRanksStore.createIndex('teamAbrvID', 'teamAbrvID', { unique: false });
        teamSeasonRanksStore.createIndex('seasonID', 'seasonID', { unique: false });
      } else {
        if (CONSOLE_ON) console.log(`${Emojis.voiceAmp} TeamSeasonRanks store already exists`);
      }
      // #endregion

      // #region SYNC-STATUS **************************************************
      if (!db.objectStoreNames.contains(Stores.SyncStatus)) {
        if (CONSOLE_ON) console.log(`${Emojis.gear} Creating SyncStatus store`);
        db.createObjectStore(Stores.SyncStatus, { keyPath: 'id' });
      } else {
        if (CONSOLE_ON) console.log(`${Emojis.voiceAmp} SyncStatus store already exists`);
      }
      // #endregion

      // #region HEAD-TO-HEAD *************************************************
      if (!db.objectStoreNames.contains(Stores.HeadToHead)) {
        if (CONSOLE_ON) console.log(`${Emojis.gear} Creating HeadToHead store`);
        db.createObjectStore(Stores.HeadToHead, { keyPath: 'headToHeadID' });
      } else {
        if (CONSOLE_ON) console.log(`${Emojis.voiceAmp} HeadToHead store already exists`);
      }
      // #endregion
      // no need to resolve here

      return true;
    };

    request.onupgradeneeded = (event: Event) => {
      // const db = (event.target as IDBRequest).result;
      processEvent(event);
    };

    request.onsuccess = (event: Event) => {
      const db = (event.target as IDBRequest).result;
      version = db.version;
      updateDbVersion(version);
      if (CONSOLE_ON) console.log(`${trSuccess} request.onsuccess - initDB`, version);
      resolve(true);
      callback();
      if (CONSOLE_ON) console.log(`${Emojis.gear} IDB Database initialized with version: ${version}`);
    };

    request.onerror = () => {
      resolve(false);
    };
  });
};

export const updateSyncStatus = async (storeName: string): Promise<void> => {
  const timestamp = new Date().toISOString();
  const data: SyncStoreProps = { id: storeName as Stores, lastUpdatedOn: Date.now(), timestamp: timestamp };
  if (CONSOLE_ON) console.log(`${Emojis.gear} updateSyncStatus for %c ${storeName} %c`, ActionCls, '');
  await updateData(Stores.SyncStatus, data);
};

// #region CRUD ***************************************************************
export const createStore = (storeName: string, keyPath: string, newVersion: number): Promise<boolean> => {
  return new Promise((resolve) => {
    // open the connection
    request = indexedDB.open(MY_NFL_STATS_DB, newVersion);
    const log = `${Emojis.gear} createStore - v.${newVersion} - name: ${storeName} - keyPath: ${keyPath}`;
    if (CONSOLE_ON) console.log(log);

    request.onupgradeneeded = (event) => {
      db = request.result;

      if (!db.objectStoreNames.contains(storeName)) {
        const msg = `${Emojis.gear} request.onupgradeneeded - When creating %c ${storeName} %c store |`;
        const msgData = `type %c ${event.type} %c version %c ${version} %c`;
        if (CONSOLE_ON) console.log(`${msg} ${msgData}`, ActionCls, '', ValueCls, '', CountDisplayCls, '');
        db.createObjectStore(storeName, { keyPath: keyPath });
      }
    };

    /* eslint-disable @typescript-eslint/no-unused-vars */
    request.onsuccess = (event) => {
      db = request.result;
      version = db.version;
      updateDbVersion(version);
      const msgData = `type %c ${event.type} %c version %c ${version} %c`;
      if (CONSOLE_ON) console.log(`${trSuccess} request.onsuccess - ${msgData}`, ValueCls, '', CountDisplayCls, '');
      resolve(true);
    };

    request.onerror = () => {
      resolve(false);
    };
  });
};

export const deleteStore = (storeName: string, newVersion: number): Promise<boolean> => {
  return new Promise((resolve) => {
    // open the connection
    request = indexedDB.open(MY_NFL_STATS_DB, newVersion);

    if (CONSOLE_ON) console.log(`${Emojis.gear} createStore - v.${newVersion} - name: ${storeName}`);

    request.onupgradeneeded = () => {
      db = request.result;

      if (db.objectStoreNames.contains(storeName)) {
        if (CONSOLE_ON) console.log(`${Emojis.delete} Attempting to delete <${storeName}> store`);
        db.deleteObjectStore(storeName);
      }
    };

    request.onsuccess = () => {
      db = request.result;
      version = db.version;
      updateDbVersion(version);
      if (CONSOLE_ON) console.log(`${trSuccess} request.onsuccess - store ${storeName} has been deleted`);
      updateSyncStatus(storeName);
      resolve(true);
    };

    request.onerror = () => {
      resolve(false);
    };
  });
};

/** Commit records atomically, updating the store-level sync marker only for complete loads. */
const writeData = <T>(storeName: string, data: T, operation: 'add' | 'put', updateSyncMarker = true): Promise<T> => {
  if (Array.isArray(data) && data.length === 0) return Promise.resolve(data);
  return new Promise((resolve, reject) => {
    const openRequest = indexedDB.open(MY_NFL_STATS_DB, version);
    openRequest.onerror = () => reject(openRequest.error ?? new Error(`Unable to open ${MY_NFL_STATS_DB}`));
    openRequest.onsuccess = () => {
      const connection = openRequest.result;
      let transaction: IDBTransaction | undefined;
      try {
        const stores = storeName === Stores.SyncStatus || !updateSyncMarker
          ? [storeName] : [storeName, Stores.SyncStatus];
        transaction = connection.transaction(stores, 'readwrite');
        const tx = transaction;
        tx.oncomplete = () => { connection.close(); resolve(data); };
        tx.onabort = tx.onerror = () => {
          connection.close();
          reject(tx.error ?? new Error(`Unable to write ${storeName}`));
        };
        const store = tx.objectStore(storeName);
        (Array.isArray(data) ? data : [data]).forEach((item) => store[operation](item));
        if (storeName !== Stores.SyncStatus && updateSyncMarker) {
          const lastUpdatedOn = Date.now();
          tx.objectStore(Stores.SyncStatus).put({
            id: storeName, lastUpdatedOn, timestamp: new Date(lastUpdatedOn).toISOString(),
          });
        }
      } catch (error) {
        transaction?.abort();
        connection.close();
        reject(error);
      }
    };
  });
};

export const addData = <T>(storeName: string, data: T): Promise<T> => writeData(storeName, data, 'add');
export const updateData = <T>(storeName: string, data: T): Promise<T> => writeData(storeName, data, 'put');

/**
 * Upserts a subset of records without marking the whole store as refreshed.
 * Existing collection freshness is preserved, including an absent sync marker.
 *
 * @param {string} storeName - Store receiving the partial update
 * @param {T} data - Records to upsert
 *
 * @return {Promise<T>} Records after the local transaction commits
 */
export const updatePartialData = <T>(storeName: string, data: T): Promise<T> =>
  writeData(storeName, data, 'put', false);

const readStore = <T>(storeName: string, key?: string): Promise<T> => new Promise((resolve, reject) => {
  const openRequest = indexedDB.open(MY_NFL_STATS_DB, version);
  openRequest.onerror = () => reject(openRequest.error ?? new Error(`Unable to open ${MY_NFL_STATS_DB}`));
  openRequest.onsuccess = () => {
    const connection = openRequest.result;
    try {
      const tx = connection.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const result = key === undefined ? store.getAll() : store.get(key);
      tx.oncomplete = () => { connection.close(); resolve(result.result); };
      tx.onabort = tx.onerror = () => {
        connection.close();
        reject(tx.error ?? new Error(`Unable to read ${storeName}`));
      };
    } catch (error) {
      connection.close();
      reject(error);
    }
  };
});

export const getStoreData = <T>(storeName: string): Promise<T[]> => readStore<T[]>(storeName);
export const getStoreDataByKey = <T>(storeName: string, key: string): Promise<T | null> =>
  readStore<T | null>(storeName, key);

export const getStoreDataByIndex = <T>(storeName: string, indexName: string): Promise<T[]> => {
  return new Promise((resolve, reject) => {
    request = indexedDB.open(MY_NFL_STATS_DB, version);

    request.onsuccess = (event: Event) => {
      const db = (event.target as IDBRequest).result;
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const index = store.index(indexName);

      const query = index.getAll(indexName);
      const msg = `Found ${query.result.length} games for season ${indexName}`;
      query.onsuccess = () => {
        if (CONSOLE_ON) console.log(`${trSuccess} ${msg}`);
        resolve(query.result);
      };

      query.onerror = () => {
        reject(query.error);
      };
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
};

export const getDataByIndex = <T>(
  storeName: string,
  indexName: string,
  value: string | number
): Promise<T[] | null> => {
  return new Promise((resolve, reject) => {
    request = indexedDB.open(MY_NFL_STATS_DB, version);

    request.onsuccess = (event: Event) => {
      const db = (event.target as IDBRequest).result;
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const index = store.index(indexName);
      const getRequest = index.getAll(value);

      getRequest.onsuccess = () => {
        resolve(getRequest.result);
      };

      getRequest.onerror = () => {
        reject(getRequest.error);
      };
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
};

export const getCount = <T>(storeName: string): Promise<T | string | null | number> => {
  return new Promise((resolve) => {
    request = indexedDB.open(MY_NFL_STATS_DB, version);

    request.onsuccess = (event: Event) => {
      const db = (event.target as IDBRequest).result;
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const recCountRequest = store.count();
      recCountRequest.onsuccess = () => {
        const log = `${trSuccess} recCountRequest.onsuccess scheduled for getting the count from ${storeName}`;
        if (CONSOLE_ON) console.log(log);
      };

      recCountRequest.oncomplete = () => {
        const log = `${trEnd} recCountRequest.oncomplete ${storeName} getCount: ${recCountRequest.result}`;
        if (CONSOLE_ON) console.log(log);
        resolve(recCountRequest.result as number);
      };

      recCountRequest.onerror = () => {
        const error = recCountRequest.error?.message;
        if (error) {
          resolve(error);
        } else {
          resolve(`${Emojis.ex} Unknown error`);
        }
      };
    };

    request.onerror = () => {
      const error = request.error?.message;
      if (error) {
        resolve(error);
      } else {
        resolve(`${Emojis.ex} Unknown error`);
      }
    };
  });
};

export const getCreatedStoreNames = <T>(): Promise<T | string | string[] | null | number> => {
  return new Promise((resolve) => {
    request = indexedDB.open(MY_NFL_STATS_DB, version);

    request.onsuccess = (event: Event) => {
      if (CONSOLE_ON) console.log(`${trSuccess} request.onsuccess - getCreatedStoreNames`);
      const db = (event.target as IDBRequest).result;
      const keys = Object.keys(Stores);
      const availableStores = keys.map((key) =>
        db.objectStoreNames.contains(Stores[key as keyof typeof Stores]) ? Stores[key as keyof typeof Stores] : ''
      );

      resolve(availableStores);
    };

    request.onerror = () => {
      const error = request.error?.message;
      if (error) {
        resolve(error);
      } else {
        resolve(`${Emojis.ex} Unknown error`);
      }
    };
  });
};

export const awaitForReadyState = (): Promise<boolean> => {
  return new Promise((resolve) => {
    let timesToRun = 6;
    const intervalID = setInterval(() => {
      if (request.readyState === 'done') {
        clearInterval(intervalID);
        resolve(true);
      } else {
        timesToRun--;
        if (timesToRun === 0) {
          clearInterval(intervalID);
          resolve(false);
        }
      }
    }, 500);
  });
};
// #endregion
